import { createInstance, dateReviver } from '@/api';

import {
    Paging,
    PublicationClient,
    PublicationVersionFilter,
    SearchResultOfPublicationVersionFilterAndPublicationVersionSearchResult,
    Sorting
} from '@/api/frontoffice/clients';

import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { i18n } from '@/i18n';

export interface PublicationVersionListStoreState {
    loading: boolean;
    publications?: SearchResultOfPublicationVersionFilterAndPublicationVersionSearchResult;
    filter?: PublicationVersionFilter;
    sorting?: Sorting;
    paging?: Paging;
}

export const client = new PublicationClient('', createInstance(), dateReviver);

const defaultFilter: PublicationVersionFilter = {    
    onlyCompleted: false
};

const defaultSorting: Sorting = {
    by: 'creationDate',
    descending: true
};

const defaultPaging: Paging = {
    pageNumber: 1,
    pageSize: 10
};

export const usePublicationVersionListStore = defineStore('publications-publication-versions-list-store', {
    state: (): PublicationVersionListStoreState => ({
        loading: false,
        publications: null,
        filter: { ...defaultFilter },
        sorting: { ...defaultSorting },
        paging: { ...defaultPaging }
    }),
    getters: {
        metaData(): {
            totalRows: number;
            resultsPerPage: number;
            currentPage: number | undefined;
            serverside: boolean;
        } {
            return {
                totalRows: this.publications?.totalcount ?? 0,
                resultsPerPage: this.paging.pageSize,
                currentPage: this.paging.pageNumber,
                serverside: true
            };
        }
    },
    actions: {
        clear() {
            this.publications = null;
            this.sorting = { ...defaultSorting };
            this.paging = { ...defaultPaging };
            this.filter = { ...defaultFilter };
            this.loading = false;
            useAlertStore().clearAlert();
        },
        resetPager() {
            this.paging.pageNumber = 1;
        },
        updatePage(pageNumber: number) {
            this.paging.pageNumber = pageNumber;
        },
        updateSorting(by: string, order: string) {
            this.sorting.by = by;
            this.sorting.descending = order !== 'Asc';
        },
        updateFilter(filter: Partial<PublicationVersionFilter>) {            
            this.filter.onlyCompleted = filter.onlyCompleted;
        },
        async getPublicationVersions(publicationId: string) {
            this.loading = true;
            useAlertStore().clearAlert();
            try {
                const response = await client.getPublicationVersions(
                    publicationId,
                    this.filter?.onlyCompleted,
                    mapToQueryableOrder(this.sorting?.by),
                    this.sorting?.descending,
                    this.paging?.pageNumber,
                    this.paging?.pageSize
                );
                this.publications = response;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('publication-versions.list.error.title'), error);
            } finally {
                this.loading = false;
            }
        }
    }
});

function mapToQueryableOrder(order: string) {
    // tweak if necesary
    switch (order) {
        default:
            return order;
    }
}
